import { yupResolver } from '@hookform/resolvers/yup'
import CancelIcon from '@mui/icons-material/Cancel'
// import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Floor } from 'interface'
import { useSnackbar } from 'notistack'
import { useLayoutEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import {
  loadImageProcessToCanvas,
  loadImageSRCProcessToCanvas,
} from 'utils/imageConvert'
import * as yup from 'yup'
import EventIcon from '@mui/icons-material/Event'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'

type Props = {
  apiEndpoint?: string
  label: string
  useGPS: boolean
  initFloors: Floor[]
}
interface FormInput {
  useGPS: boolean
}

export default function EditUseGPS({
  apiEndpoint,
  useGPS,
  initFloors,
  label,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    useGPS: yup.boolean().required(),
  })
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      useGPS,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const [removeFloors, setRemoveFloors] = useState<number[]>([])
  const ref = useRef<HTMLDivElement | null>(null)
  const inputFloorRef = useRef({} as HTMLInputElement)
  const [floors, setFloors] = useState<
    {
      id?: number
      name: string
      image?: HTMLCanvasElement
    }[]
  >([])
  useLayoutEffect(() => {
    const tmp: {
      id: number
      name: string
      image?: HTMLCanvasElement
    }[] = []
    for (let i = 0; i < initFloors.length; i++) {
      const f = initFloors[i]
      loadImageSRCProcessToCanvas(f.floorImage).then((image) => {
        tmp[i] = {
          id: f.id,
          name: f.floorName,
          image,
        }
      })
    }
    setFloors(tmp)
  }, [initFloors])
  useLayoutEffect(() => {
    if (!edit) {
      const tmp: {
        id: number
        name: string
        image?: HTMLCanvasElement
      }[] = []
      for (let i = 0; i < initFloors.length; i++) {
        const f = initFloors[i]
        loadImageSRCProcessToCanvas(f.floorImage).then((image) => {
          tmp[i] = {
            id: f.id,
            name: f.floorName,
            image,
          }
        })
      }
      setFloors(tmp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        let formData = new FormData()
        for (const { image, name } of floors) {
          if (!image || !name) continue
          await new Promise((rs) => {
            image.toBlob((blob) => {
              if (blob) {
                const ext = blob.type.split('/').pop()
                let file = new File(
                  [blob],
                  'floorImage' + new Date().getTime() + '.' + ext,
                  {
                    type: blob.type,
                  }
                )
                formData.append('updateFloorImage', file)
                rs(1)
              }
            })
          })
        }
        formData.append(
          'data',
          JSON.stringify({
            ...data,
            updateFloors: floors.map((v, i) => ({
              id: v.id,
              name: v.name,
              order: i + 1,
            })),
            removeFloors,
          })
        )
        await axiosClient.put(apiEndpoint, formData)
        setEdit(false)
        const msg = 'チェックポイントの' + label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        setLoading(false)
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={2}
    >
      {edit ? (
        <>
          <Stack
            direction={{ md: 'row', xs: 'column' }}
            alignItems="center"
            sx={{ display: edit ? 'flex' : 'none' }}
            ref={ref}
            spacing={2}
          >
            <Stack
              direction={{ md: 'row', xs: 'column' }}
              flex={1}
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color="secondary"
                    checked={getValues('useGPS')}
                    onChange={(e) => {
                      setValue('useGPS', e.target.checked, {
                        shouldValidate: true,
                      })
                      if (floors.length === 0) {
                        setFloors([
                          {
                            name: '',
                          },
                        ])
                      }
                      if (e.target.checked) {
                        const tmp: {
                          id: number
                          name: string
                          image?: HTMLCanvasElement
                        }[] = []
                        for (let i = 0; i < initFloors.length; i++) {
                          const f = initFloors[i]
                          loadImageSRCProcessToCanvas(f.floorImage).then(
                            (image) => {
                              tmp[i] = {
                                id: f.id,
                                name: f.floorName,
                                image,
                              }
                            }
                          )
                        }
                        setFloors(tmp)
                      }
                    }}
                  />
                }
                sx={{
                  color: 'red',
                }}
                label={label}
              />
            </Stack>
            <Stack
              spacing={4}
              direction={{ md: 'column', xs: 'row-reverse' }}
              flex={'none'}
            >
              <Button
                sx={{
                  px: 1,
                  minWidth: 0,
                }}
                size="small"
                color="error"
                variant="contained"
                onClick={() => {
                  setEdit(false)
                  setValue('useGPS', useGPS, {
                    shouldValidate: true,
                  })
                }}
              >
                <CancelIcon />
              </Button>
              <LoadingButton
                disabled={Object.keys(errors).length > 0}
                loading={loading}
                sx={{
                  px: 1,
                  minWidth: 0,
                }}
                size="small"
                variant="contained"
                type="submit"
              >
                <SaveAsIcon />
              </LoadingButton>
            </Stack>
          </Stack>
          {!getValues('useGPS') && (
            <Stack>
              <Typography variant="body1" component="p" fontWeight="bold">
                ガイドマップのアップロード
              </Typography>
              {floors.map((v, i) => {
                return (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    key={i}
                  >
                    <Stack flex={1}>
                      <TextField
                        required
                        margin="dense"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EventIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                        label="フロア名"
                        fullWidth
                        variant="standard"
                        value={v.name}
                        onChange={(e) => {
                          const tmp = [...floors]
                          tmp.splice(i, 1, {
                            ...tmp[i],
                            name: e.target.value,
                          })
                          setFloors(tmp)
                        }}
                      />
                      {v.image && (
                        <Stack my={2}>
                          <img
                            src={v.image.toDataURL()}
                            alt="画像"
                            style={{
                              border: '1px solid #999',
                              width: v.image.width,
                              maxWidth: '100%',
                            }}
                          ></img>
                        </Stack>
                      )}
                      <Stack my={2} direction="row" spacing={2}>
                        <input
                          ref={inputFloorRef}
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={'upload-floor' + i}
                          type="file"
                          onChange={async (e) => {
                            if (e.target.files) {
                              const tmp = [...floors]
                              tmp.splice(i, 1, {
                                ...tmp[i],
                                image: await loadImageProcessToCanvas(
                                  e.target.files[0]
                                ),
                              })
                              setFloors(tmp)
                              if (inputFloorRef.current)
                                inputFloorRef.current.value = ''
                            }
                          }}
                        />

                        <label
                          htmlFor={'upload-floor' + i}
                          style={{ width: 'max-content', marginLeft: 0 }}
                        >
                          <Button
                            variant="contained"
                            component="span"
                            size="small"
                            color="secondary"
                            sx={{
                              minWidth: 150,
                            }}
                          >
                            {v.image ? '再アップロード' : 'アップロード'}
                          </Button>
                        </label>
                      </Stack>
                    </Stack>
                    <Stack
                      width={'3rem'}
                      direction="row"
                      justifyContent="center"
                    >
                      <div>
                        {floors.length !== 1 && (
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() => {
                              const tmp = [...floors]
                              tmp.splice(i, 1)
                              setFloors(tmp)
                              const id = initFloors.find(
                                (f) => f.id === v.id
                              )?.id
                              if (id) {
                                setRemoveFloors([...removeFloors, id])
                              }
                            }}
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        )}
                      </div>
                    </Stack>
                  </Stack>
                )
              })}
              <Stack direction="row" justifyContent="center">
                <div>
                  <IconButton
                    aria-label="add"
                    color="primary"
                    size="large"
                    onClick={() => {
                      setFloors([...floors, { name: '' }])
                    }}
                  >
                    <AddCircleIcon fontSize="large" />
                  </IconButton>
                </div>
              </Stack>
            </Stack>
          )}
        </>
      ) : (
        <>
          <Stack
            direction={{ md: 'row', xs: 'column' }}
            spacing={2}
            sx={{ display: !edit ? 'flex' : 'none' }}
            alignItems={{
              md: 'center',
              xs: 'flex-start',
            }}
          >
            <Typography fontWeight="bold" variant="body1" flex={'none'}>
              {label}
            </Typography>
            <Stack direction="row" alignItems="center">
              <Typography
                fontWeight="bold"
                variant="body1"
                sx={{
                  color: (theme) =>
                    useGPS
                      ? theme.palette.success.main
                      : theme.palette.warning.dark,
                }}
              >
                {useGPS ? '有効' : '無効'}
              </Typography>
              {/* <Button
                sx={{
                  flex: 'none',
                  mx: 1,
                  p: 0.5,
                  minWidth: 0,
                }}
                size="small"
                variant="contained"
                color="info"
                onClick={() => {
                  setEdit(true)
                }}
              >
                <ModeEditIcon fontSize="small" />
              </Button> */}
            </Stack>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            gap={1}
          >
            {!useGPS &&
              initFloors.map((v, i) => {
                return (
                  <Stack key={i} alignItems="center">
                    <Typography
                      variant="body1"
                      component="h4"
                      sx={{
                        flex: 1,
                      }}
                      className="mb-4 md:mb-0"
                    >
                      {v.floorName}
                    </Typography>
                    <img
                      loading="lazy"
                      src={v.floorImage}
                      alt="フロア画像"
                      style={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        maxHeight: '250px',
                        objectFit: 'contain',
                      }}
                      crossOrigin="anonymous"
                    ></img>
                  </Stack>
                )
              })}
          </Stack>
        </>
      )}
    </Box>
  )
}
